/**
 * Public demo routes — pages that are reachable without a BetterAuth
 * session and that should NOT trigger network calls to the API for
 * `/auth/get-session` or `/companies/current-user`.
 *
 * These calls are harmless when the API is up (BetterAuth returns an
 * unauthenticated session for the get-session call), but on a developer
 * machine where the API isn't running (or port 5000 is taken by macOS
 * Control Center / AirPlay Receiver, which is the default config on
 * macOS Monterey+), the browser logs 6 CORS errors per page load. Those
 * errors fail PRD-1 AC1 ("all 7 tabs render without console errors") and
 * make the demo look broken when a tester opens DevTools.
 *
 * On these routes we skip the fetches entirely. The auth context still
 * renders, just with `user = null` and `session = null` (which is the
 * correct state for an anonymous visitor anyway).
 *
 * Gap-close #3 (P0) — PRD-1.
 */

/** Path prefixes that render a Toyota / customer-demo experience and do
 *  not require auth. Add to this list if you add new public demo views. */
const PUBLIC_DEMO_PREFIXES = [
  '/toyota-compliance',
  '/bmw-compliance',
  '/ford-compliance',
  '/mercedes-compliance',
  '/tremec-compliance',
  '/gdls-compliance',
  '/metalsa-compliance',
  '/nexteer',
  '/katcon',
] as const;

/**
 * True iff the current `window.location.pathname` is a public demo route.
 * Safe to call during SSR (returns false if `window` is undefined).
 */
export function isPublicDemoRoute(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!p) return false;
  return PUBLIC_DEMO_PREFIXES.some((prefix) => p.startsWith(prefix));
}
